import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaPrint, FaFileAlt, FaImage, 
  FaListUl, FaCheckCircle, FaChevronRight,
  FaTimes 
} from 'react-icons/fa';

export default function QuotationNewReview() {
    const { id, versionNumber } = useParams();
    const [showFormatSelection, setShowFormatSelection] = useState(true);
    const [printType, setPrintType] = useState('with-qr');
    const [loading, setLoading] = useState(true);
    const [quotation, setQuotation] = useState(null);

    const printTypes = [
      {
        id: 'with-qr',
        title: 'Standard Print (QR)',
        description: 'Complete quotation with QR code for easy payments',
        icon: <FaFileAlt size={24} />,
        color: 'blue',
        withQR: true,
        shortcut: '1'
      },
      {
        id: 'without-qr',
        title: 'Standard Print (No QR)',
        description: 'Complete quotation without QR code, bank details in header, sales coordinators in one line',
        icon: <FaFileAlt size={24} />,
        color: 'red',
        withQR: false,
        shortcut: '2'
      }
    ];

  useEffect(() => {
    const fetchQuotationData = async () => {
      try {
        setLoading(true);
        const endpoint = versionNumber 
          ? `/quotation-new/version/${id}/${versionNumber}`
          : `/quotation-new/${id}`;
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`);
        if (response.data) {
          setQuotation(response.data);
        }
      } catch (error) {
        console.error('Error fetching quotation:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuotationData();
  }, [id, versionNumber]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!showFormatSelection) return;
      
      const key = event.key;
      const selectedType = printTypes.find(type => type.shortcut === key);
      
      if (selectedType) {
        event.preventDefault();
        handleTypeSelect(selectedType.id);
      }
      
      // ESC key to toggle format selection
      if (key === 'Escape') {
        setShowFormatSelection(!showFormatSelection);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [showFormatSelection, printTypes]);    // Determine if this is a version print or regular quotation print
    const generatePdfUrl = (type) => {
      const selectedType = printTypes.find(t => t.id === type);
      const printWithQR = selectedType ? selectedType.withQR : true;
      
      const apiEndpoint = versionNumber 
          ? `/quotation-new/version/${id}/${versionNumber}?printWithQR=${printWithQR}`
          : `/quotation-new/data/${id}?printWithQR=${printWithQR}`;
      
      return `https://rlugroup.in/pdf/convert?fileName=rlugroup.hbs&url=${process.env.REACT_APP_API_BASE_URL}${apiEndpoint}`;
    };

    const handleTypeSelect = (type) => {
      setPrintType(type);
      setShowFormatSelection(false);
    };

    const FormatSelectionModal = () => (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-11/12 md:w-3/4 lg:w-1/2 p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold flex items-center">
              <FaPrint className="mr-2 text-gray-600" />
              Select Print Format
              <span className="ml-3 text-sm font-normal text-gray-500">(Use number keys 1-2 or ESC)</span>
            </h3>
            <button 
              className="text-gray-500 hover:text-gray-700 focus:outline-none transition-all duration-200 hover:scale-110 active:scale-95"
              onClick={() => setShowFormatSelection(false)}
            >
              <FaTimes size={24} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {printTypes.map((type) => (
              <div 
                key={type.id}
                className={`bg-gradient-to-br from-${type.color}-50 to-${type.color}-100 rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl hover:scale-105 cursor-pointer border-2 group ${
                  printType === type.id ? `border-${type.color}-500 bg-gradient-to-br from-${type.color}-100 to-${type.color}-200` : `border-${type.color}-200 hover:border-${type.color}-400`
                } hover:bg-gradient-to-br hover:from-${type.color}-100 hover:to-${type.color}-200`}
                onClick={() => handleTypeSelect(type.id)}
              >
                <div className="p-6 text-center transition-all duration-300 group-hover:transform group-hover:scale-105">
                  <div className={`mx-auto w-16 h-16 rounded-full bg-${type.color}-200 border-2 border-${type.color}-300 flex items-center justify-center mb-4 relative transition-all duration-300 group-hover:scale-110 group-hover:bg-${type.color}-300 group-hover:border-${type.color}-400`}>
                    <div className={`text-${type.color}-700 transition-all duration-300 group-hover:text-${type.color}-800`}>
                      {type.icon}
                    </div>
                    {!type.withQR && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">QR</span>
                        <div className="absolute inset-0 bg-red-500 rounded-full transform rotate-45"></div>
                        <span className="absolute text-white text-xs font-bold z-10">✕</span>
                      </div>
                    )}
                  </div>
                  <h4 className={`text-lg font-medium mb-2 transition-all duration-300 text-${type.color}-800 group-hover:text-${type.color}-900`}>
                    {type.title}
                    <span className={`ml-2 inline-flex items-center justify-center w-6 h-6 bg-${type.color}-300 text-${type.color}-800 text-xs rounded-full font-bold group-hover:bg-${type.color}-400 group-hover:text-${type.color}-900`}>
                      {type.shortcut}
                    </span>
                  </h4>
                  <p className={`text-${type.color}-600 text-sm mb-6 transition-all duration-300 group-hover:text-${type.color}-700`}>{type.description}</p>
                  <button 
                    className={`w-full py-2 px-4 rounded text-sm font-medium flex items-center justify-center transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                      printType === type.id 
                        ? `bg-${type.color}-600 text-white shadow-lg hover:bg-${type.color}-700 hover:shadow-xl border-2 border-${type.color}-700` 
                        : `bg-${type.color}-200 text-${type.color}-800 border-2 border-${type.color}-400 hover:bg-${type.color}-300 hover:border-${type.color}-500 hover:text-${type.color}-900 hover:shadow-md`
                    }`}
                  >
                    {printType === type.id ? (
                      <>
                        <FaCheckCircle className="mr-2" /> Selected
                      </>
                    ) : (
                      <>
                        Select <FaChevronRight className="ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    return (
        <>
          {/* Format Selection Modal - Shows by default */}
          {showFormatSelection && <FormatSelectionModal />}

          {/* PDF Viewer */}
          <div className={`h-screen w-full ${showFormatSelection ? 'hidden' : 'block'}`}>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <object
                data={generatePdfUrl(printType)}
                type="application/pdf"
                className="w-full h-full"
                aria-label="Quotation PDF"
              >
                <div className="text-center p-8">
                  <p className="mb-4">Your browser doesn't support embedded PDFs.</p>
                  <a 
                    href={generatePdfUrl(printType)} 
                    target="_blank" 
                    rel="noreferrer"
                  >
                    Download the PDF
                  </a>
                </div>
              </object>
            )}
          </div>
        </>
    )
}